import { BrowserRuntime } from './browser-runtime'

/**
 * Rejection reason when the visitor dismisses the provider tab instead of finishing. Lives here
 * because the shared runtime's wait never rejects. Only this one watches the tab.
 */
export const SIGNIN_CANCELLED = 'signin-cancelled'

/** As long as a provider's sign-in state lives (Slack's is 10 minutes); a later answer is refused anyway. */
const BACKGROUND_WAIT_MS = 10 * 60 * 1000
/** A provider page that cuts its tab off does it as the tab first loads; a visitor closes it later. */
const CUT_OFF_WINDOW_MS = 5000

export interface IOpenAndWaitOptions {
  /**
   * Opts in to waiting on a tab the provider cut off, which this page can no longer see open or
   * closed. Called once when that happens so the page can stop spinning; the wait carries on.
   */
  onTabOutOfSight?: () => void
}

// A wait carried on in the background after its tab was cut off. Starting another one replaces
// it, and the replaced wait is never answered, so nothing downstream runs or reports for it.
let dropBackgroundWait: (() => void) | null = null

// don't make this async, it will break browser security
const openAndWaitForMessage = (url: string, messageType: string, options: IOpenAndWaitOptions = {}): Promise<any> => {
  let callback
  let cancel
  let closePoll

  dropBackgroundWait?.()

  const stopWaiting = () => {
    window.removeEventListener('message', windowListener)
    window.removeEventListener('storage', storageListener)
    window.clearInterval(closePoll)
    if (dropBackgroundWait === stopWaiting) dropBackgroundWait = null
  }

  // Opened before listening, still in the same synchronous call, so the listener knows its tab.
  const openWindow = window.open(url)
  const openedAt = Date.now()

  // Only this tab's redirect page, served from this origin, may answer. Any other page that has
  // a handle on this one could otherwise post a payload of the right type and finish the sign-in
  // with it, and Google's flow carries no state to catch that later.
  const windowListener = (event: MessageEvent) => {
    if (event.origin !== window.location.origin || !openWindow || event.source !== openWindow) return
    if (event.data?.type === messageType) {
      stopWaiting()
      callback(event.data.payload)
    }
  }

  const storageListener = (windowData) => {
    const state = new URL(url).searchParams.get('state')
    if (windowData && windowData.type === 'storage' && windowData.key === state) {
      stopWaiting()
      if (state && localStorage.getItem(state)) {
        localStorage.removeItem(state)
      }
      callback(new URL(windowData.url).search)
    }
  }

  window.addEventListener('message', windowListener)
  window.addEventListener('storage', storageListener)

  let inBackground = false
  // The provider tab reports nothing when it is dismissed, so its disappearance is the only
  // sign the visitor walked away. A blocked popup is never there to begin with.
  closePoll = window.setInterval(() => {
    if (inBackground) {
      if (Date.now() - openedAt >= BACKGROUND_WAIT_MS) stopWaiting()
      return
    }
    if (openWindow && !openWindow.closed) return

    // A provider page with Cross-Origin-Opener-Policy (Slack's workspace sign-in) cuts the tab
    // off, and it reads as closed from then on, open or not. The cut shows while the tab is still
    // loading or while the visitor is away in it; a close is the visitor back here after that.
    // Only the cut keeps an opted-in caller waiting, answered through localStorage.
    const looksCutOff = Date.now() - openedAt < CUT_OFF_WINDOW_MS || !document.hasFocus()
    if (openWindow && options.onTabOutOfSight && looksCutOff) {
      inBackground = true
      dropBackgroundWait = stopWaiting
      options.onTabOutOfSight()
      return
    }

    stopWaiting()
    cancel(SIGNIN_CANCELLED)
  }, 500)

  return new Promise((resolve, reject) => {
    callback = resolve
    cancel = reject
  })
}

/**
 * The runtime the new sign-in and sign-up pages use. Everything but the provider-tab wait is the
 * shared runtime's; the wait can opt in to surviving a tab the provider cut off.
 */
export const BrowserRuntimeV2 = () => Object.freeze({ ...BrowserRuntime(), openAndWaitForMessage })

export type IBrowserRuntimeV2 = ReturnType<typeof BrowserRuntimeV2>
